import { AlertCircleIcon, ArchiveIcon, ClockIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { ALERT_SECTION_LABELS } from '../../utils/alert/alerts';
import { SIDE_PANEL_TRANSITION_DURATION_STYLE } from '../../utils/sidePanelMotion';

import type { AlertStatusSection } from '../../utils/alert/alerts';

const ICON_BY_SECTION: Record<AlertStatusSection, React.ComponentType<{ className?: string }>> = {
  needAttention: AlertCircleIcon,
  pending: ClockIcon,
  history: ArchiveIcon,
};

interface IAlertSectionProps {
  section: AlertStatusSection;
  /**
   * Total alerts in this status group — the per-status count query, not the
   * number of rows passed in. Paginated sections show more here than on screen.
   */
  count: number;
  /** The rows themselves, typically `AlertListItem` elements. */
  children: React.ReactNode;
  /**
   * Renders the labeled header above the rows. `false` leaves the rows column
   * in place and drops the header, so a surface can show a group's rows
   * without its label and still keep the same nodes when the label returns.
   * @default true
   */
  labeled?: boolean;
  /**
   * Whether a mounted label is open. `false` keeps it in the tree at zero
   * height and transparent; flipping it slides the label open (at the panel's
   * duration) and fades it in, so a surface can grow a label above rows that
   * are already on screen without them jumping.
   * @default true
   */
  labelRevealed?: boolean;
  className?: string;
}

/**
 * One status group of the Alerts Center: a labeled header with the group's
 * total, above the rows it was handed. Always expanded. Renders whatever it is
 * given, so callers drop the sections they have no rows for.
 */
const AlertSection = ({
  section,
  count,
  children,
  labeled = true,
  labelRevealed = true,
  className,
}: IAlertSectionProps) => {
  const Icon = ICON_BY_SECTION[section];

  return (
    <div className={cx('flex flex-col', className)}>
      {labeled ? (
        // A `0fr`/`1fr` grid track is the one height that transitions to its
        // content's size; the inner wrapper clips while the track is closed.
        <div
          style={SIDE_PANEL_TRANSITION_DURATION_STYLE}
          className={cx(
            'grid transition-[grid-template-rows] ease-in-out motion-reduce:transition-none',
            labelRevealed ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}
        >
          <div className="min-h-0 overflow-hidden">
            <div
              aria-hidden={labelRevealed ? undefined : true}
              className={cx(
                'text-secondary mb-3 flex items-center gap-1.5 py-2 text-xs font-medium uppercase',
                'transition-opacity duration-150 ease-in-out motion-reduce:transition-none',
                labelRevealed ? 'opacity-100' : 'opacity-0'
              )}
            >
              <Icon className="size-3.5 shrink-0" />
              <span>
                {ALERT_SECTION_LABELS[section]} ({count})
              </span>
            </div>
          </div>
        </div>
      ) : null}
      {/* No gap: the rows rule themselves off from each other. */}
      <div className="flex flex-col">{children}</div>
    </div>
  );
};

export default AlertSection;
